import { Alert, Box, IconButton, ThemeProvider } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import HomeLayout from './layouts/HomeLayout';
import ProfilePage from './pages/accounts/ProfilePage';
import Signin from './pages/accounts/Signin';
import Signup from './pages/accounts/Signup';

import Homepage from './pages/Homepage';
import Invoicepage from './pages/Invoicepage';
import Createjob from './pages/job/create/Createjob';
import JobPage from './pages/job/JobPage';
import PhotoSettings from './pages/photosettings/PhotoSettings';
import Reportpage from './pages/Reportpage';
import Subscription from './pages/Subscription';
import Services from './pages/services/Services';
import State from './pages/exp/state/State';
import SignUpPage from './pages/exp/formik/SignUpPage';
import SignUpUsingHook from './pages/exp/formik/SignUpUsingHook';
import FormikMui from './pages/exp/formik/FormikMui';
import InternalServerError from './pages/errorpages/InternalServerError';
import NotFound from './pages/errorpages/NotFound';
import Activate from './pages/accounts/Activate';
import Resend from './pages/accounts/Resend';
import Storage from './pages/storage/Storage';
import Paypal from './pages/exp/paypal/Paypal';
import Draw from './pages/exp/draw/Draw';
import BasePreferences from './pages/basepreferences/BasePreferences';
import TurnAroundTime from './pages/turnaroundtime/TurnAroundTime';
import Admin from './pages/admin/Admin';
import Braintree from './pages/exp/paypal/Braintree';
import BraintreeDropIn from './pages/exp/paypal/BraintreeDropIn';
import Transaction from './pages/transaction/Transaction';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import UserListing from './pages/user/UserListing';
import ResetPassword from './pages/accounts/ResetPassword';
import { SnackbarProvider, useSnackbar } from 'notistack';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import Dirupload from './pages/exp/dirupload/Dirupload';
import Report from './pages/report/Report';
import JobsHome from './pages/job/JobsHome';
import FileUpload from './pages/exp/Chunk';
import EditInvoice from './pages/EditInvoice';
import ForgotPassword from './pages/accounts/ForgotPassword';
import MobilePayment from './pages/subscriptions/MobilePayment';
import PaymentSuccess from './pages/subscriptions/PaymentSuccess';
import PaymentFailed from './pages/subscriptions/PaymentFailed';
import useAuth from './hooks/use-auth';
import { ROLE } from './constants';

const SnackbarCloseButton = ({ snackbarKey }) => {
    const { closeSnackbar } = useSnackbar();
    return (
        <IconButton onClick={() => closeSnackbar(snackbarKey)}>
            <CloseOutlinedIcon color="action" sx={{ fontSize: '20px' }} />
        </IconButton>
    );
}

const App = () => {
    const { protector } = useAuth()
    const current_theme = useSelector(state => state.ui.isDarkMode)
    const theme = createTheme({
        palette: {
            mode: current_theme ? 'dark' : 'light',
            ...(
                current_theme ?
                    {
                        // Dark theme color
                        primary: {
                            light: "#7986cb",
                            main: "#3f51b5",
                            dark: "#303f9f",
                            contrastText: "#fff"
                        },
                        background: {
                            card: "#5a5a5a",
                            primary: "#3f51b5",
                            sidebar: "#1e1e1e",
                            secondary: "#D0D1FF",
                            stripped: "#707070",
                            dark: "#121212",
                            selected: "#D0D1FF"
                        },
                        text: {
                            title: "#fff",
                        },
                        custom: {
                            border: "#8a9cff",
                        }
                    } :
                    {
                        // Light theme color
                        primary: {
                            light: "#7986cb",
                            main: "#0B1345",
                            dark: "#0B1345",
                            contrastText: "#fff"
                        },
                        secondary: {
                            light: "#7986cb",
                            main: "#f764a3",
                            dark: "#303f9f",
                        },
                        background: {
                            default: "#f6f8ffbf",
                            card: "#fff",
                            primary: "#0B1345",
                            secondary: "#f764a3",
                            sidebar: "#d4d9ff",
                            stripped: "#f7f7f7",
                            light: "#f6f6f6",
                            selected: "#D0D1FF"
                        },
                        text: {
                            title: "#3f51b5",
                        },
                        custom: {
                            border: "#8a9cff",
                        }
                    }
            )

        },
        typography: {
            fontFamily: [
                'Poppins',
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
                '"Apple Color Emoji"',
                '"Segoe UI Emoji"',
                '"Segoe UI Symbol"',
            ].join(','),
            // body1: {
            //     fontWeight: 500,
            // }
        },
    });

    return (
        <SnackbarProvider maxSnack={3} action={snackbarKey => <SnackbarCloseButton snackbarKey={snackbarKey} />} >
            <ThemeProvider theme={theme}>
                <Box className={current_theme ? 'dark-mode' : 'light-mode'}>
                    <Routes >
                        <Route path="/mobilepay" element={<MobilePayment />} />
                        <Route element={<HomeLayout />}>
                            <Route path="accounts/*">
                                <Route path="signin" element={<Signin />} />
                                <Route path='forgot_password' element={<ForgotPassword />} />
                                <Route path="signup" element={<Signup />} />
                                <Route path="activate" element={<Activate />} />
                                <Route path="resend" element={<Resend />} />
                                <Route path="reset_password" element={<ResetPassword />} />
                                <Route path="*" element={<NotFound />} />
                            </Route>
                        </Route>
                        {process.env.NODE_ENV === "development"
                            &&
                            <Route path="exp/*">
                                <Route path="state" element={<State />} />
                                <Route path="formik" element={<SignUpPage />} />
                                <Route path="formik/signup" element={<SignUpUsingHook />} />
                                <Route path="formik/mui" element={<FormikMui />} />
                                <Route path="paypal" element={<Paypal />} />
                                <Route path="braintree" element={<Braintree />} />
                                <Route path="braintreeD" element={<BraintreeDropIn />} />
                                <Route path="draw" element={<Draw />} />
                                <Route path="dirupload" element={<Dirupload />} />
                                <Route path='chunk' element={<FileUpload />} />
                                {/* <Route path='time' element={<Time />} /> */}
                                <Route path="*" element={<NotFound />} />

                            </Route>
                        }
                        <Route errorElement={<InternalServerError />} element={<MainLayout />}>
                            {protector([ROLE.sa, ROLE.admin]) && <Route path="Admin/*">
                                <Route path="" index={true} element={<Admin />} />
                            </Route>}
                            <Route path="/" index={true} element={<Homepage />} />
                            <Route path="/jobs" element={<JobsHome />} />
                            <Route path="/reports" element={<Reportpage />} />
                            <Route path="/invoices" element={<Invoicepage />} />
                            <Route path='/editinvoice' element={<EditInvoice />} />
                            <Route path="/wallet" element={<Subscription />} />
                            <Route path="/jobs/:id" element={<JobPage />} />
                            <Route path="/jobs/create/:draftId" element={<Createjob />} />
                            <Route path="/photosettings" element={<PhotoSettings />} />
                            <Route path="/basepreferences" element={<BasePreferences />} />
                            <Route path="/turnaroundtime" element={<TurnAroundTime />} />
                            <Route path="/storage" element={<Storage />} />
                            <Route path="/transaction" element={<Transaction />} />
                            {protector([ROLE.sa, ROLE.admin]) && <Route path="/user" element={<UserListing />} />}
                            <Route path="/report" element={<Report />} />
                            <Route path="accounts/*">
                                <Route path="profile" element={<ProfilePage />} />
                            </Route>
                            <Route path="/services/*">
                                <Route index={true} element={<Services />} />
                                <Route path="*" element={<NotFound />} />
                            </Route>
                        </Route>
                        <Route path="/payment/success" element={<PaymentSuccess />} />
                        <Route path="/payment/failed" element={<PaymentFailed />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Box>
            </ThemeProvider>
        </SnackbarProvider>
    );
}

export default App;
